import {
  AgeBand,
  type AgeBandSelection,
  type SpecificAgeBand,
} from "@b2bportal/rbc-activities-api";
import { ensureExhaustive } from "@overrides/utilities";
import type { TFunction } from "i18next";

/**
 * Sanitizes a paxMix array for use in API requests:
 * - Deduplicates entries by ageBand (summing numberOfTravelers)
 * - Filters out entries with numberOfTravelers <= 0
 *
 * The UI keeps zero-count ageBands in selectedParticipants for the picker,
 * but the backend / Viator should only receive bands with at least one traveler.
 */
export function buildPaxMixForRequest(
  paxMix: Array<AgeBandSelection>
): Array<AgeBandSelection> {
  const totals = new Map<AgeBand, number>();
  for (const { ageBand, numberOfTravelers } of paxMix) {
    totals.set(ageBand, (totals.get(ageBand) ?? 0) + numberOfTravelers);
  }
  return [...totals.entries()]
    .filter(([, count]) => count > 0)
    .map(([ageBand, numberOfTravelers]) => ({ ageBand, numberOfTravelers }));
}

export function getParticipantCountFromAgeBandSelection(
  ageBandSelection: Array<{ numberOfTravelers?: number }> | null | undefined
): number {
  return (
    ageBandSelection?.reduce(
      (sum, band) => sum + (band.numberOfTravelers ?? 0),
      0
    ) ?? 0
  );
}

export const getAgeBandLabel = (
  { ageBand, numberOfTravelers }: SpecificAgeBand,
  t: TFunction
) => {
  switch (ageBand) {
    case AgeBand.Adult:
      return t("core-activities.search.participantPicker.adult", {
        count: numberOfTravelers,
      });
    case AgeBand.Child:
      return t("core-activities.search.participantPicker.child", {
        count: numberOfTravelers,
      });
    case AgeBand.Infant:
      return t("core-activities.search.participantPicker.infant", {
        count: numberOfTravelers,
      });
    case AgeBand.Senior:
      return t("core-activities.search.participantPicker.senior", {
        count: numberOfTravelers,
      });
    case AgeBand.Traveler:
      return t("core-activities.search.participantPicker.traveler", {
        count: numberOfTravelers,
      });
    case AgeBand.Unknown:
      return t("core-activities.search.participantPicker.unknown", {
        count: numberOfTravelers,
      });
    case AgeBand.Youth:
      return t("core-activities.search.participantPicker.youth", {
        count: numberOfTravelers,
      });
    default:
      return ensureExhaustive(ageBand);
  }
};
